import React, { useState } from 'react';
import {
  format,
  subMonths,
  addMonths,
  isSameDay,
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  addDays,
  isSameMonth,
} from 'date-fns';

interface DateRangeCalendarProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (start: Date | null, end: Date | null) => void;
}

const DateRangeCalendar: React.FC<DateRangeCalendarProps> = ({
  startDate,
  endDate,
  onChange,
}) => {
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));

  // Generate calendar days
  const generateDays = (monthDate: Date) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const start = new Date(firstDay);
    start.setDate(firstDay.getDate() - firstDay.getDay());

    // Check if we need 6 weeks or 5 weeks
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const totalDaysNeeded = firstDay.getDay() + lastDayOfMonth.getDate();
    const rowsNeeded = Math.ceil(totalDaysNeeded / 7);
    const numDays = rowsNeeded * 7;

    const days = [];
    for (let i = 0; i < numDays; i++) {
      days.push(addDays(start, i));
    }

    return days;
  };

  // Handle date click
  const handleDateClick = (date: Date) => {
    if (!startDate || (startDate && endDate)) {
      onChange(date, null);
    } else {
      if (date < startDate) {
        onChange(date, startDate);
      } else {
        onChange(startDate, date);
      }
    }
  };

  const isInRange = (date: Date) => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  // Months
  const prevMonth = subMonths(currentMonth, 1);
  const leftDays = generateDays(prevMonth);
  const rightDays = generateDays(currentMonth);

  const applyQuickRange = (
    start: Date,
    end: Date,
    updateMonth: Date = start
  ) => {
    onChange(startOfDay(start), endOfDay(end));
    setCurrentMonth(startOfMonth(updateMonth));
  };

  // Reusable calendar UI
  const renderCalendar = (month: Date, days: Date[]) => (
    <div className="w-[280px]">
      {/* Month Title */}
      <div className="text-center font-semibold py-2">
        {format(month, 'MMMM yyyy')}
      </div>

      {/* Days Header */}
      <div className="grid grid-cols-7 text-xs text-center text-gray-500 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {/* Dates */}
      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const isCurrentMonth = isSameMonth(day, month);

          if (!isCurrentMonth) {
            return <div key={i} className="h-9" />;
          }

          const selected =
            (startDate && isSameDay(day, startDate)) ||
            (endDate && isSameDay(day, endDate));

          return (
            <button
              key={i}
              type="button"
              onClick={() => handleDateClick(day)}
              className={`
                h-9 text-xs rounded-md
                ${isInRange(day) ? 'bg-teal/10' : ''}
                ${selected ? 'bg-teal text-white font-semibold' : 'hover:bg-gray-100'}
              `}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="border rounded-lg overflow-hidden bg-white shadow-md w-[620px]">
      {/* Header */}
      <div className="flex text-[28px] items-center justify-between bg-teal text-white p-0">
        <button
          className="p-3"
          onClick={() =>
            setCurrentMonth(startOfMonth(subMonths(currentMonth, 1)))
          }
        >
          ‹
        </button>
        <h4 className="text-base font-bold">
          {format(prevMonth, 'MMM yyyy')} - {format(currentMonth, 'MMM yyyy')}
        </h4>
        <button
          className="p-3"
          onClick={() =>
            setCurrentMonth(startOfMonth(addMonths(currentMonth, 1)))
          }
        >
          ›
        </button>
      </div>

      {/* Quick Filters */}
      <div className="grid grid-cols-2 gap-2 p-3 border-b bg-gray-50">
        <button
          className="text-xs border rounded px-2 py-1 hover:bg-white"
          onClick={() => applyQuickRange(new Date(), new Date(), new Date())}
        >
          Today
        </button>
        <button
          className="text-xs border rounded px-2 py-1 hover:bg-white"
          onClick={() => {
            const yesterday = subDays(new Date(), 1);
            applyQuickRange(yesterday, yesterday, yesterday);
          }}
        >
          Yesterday
        </button>
        <button
          className="text-xs border rounded px-2 py-1 hover:bg-white"
          onClick={() => applyQuickRange(subDays(new Date(), 6), new Date())}
        >
          Last 7 Days
        </button>
        <button
          className="text-xs border rounded px-2 py-1 hover:bg-white"
          onClick={() => applyQuickRange(subDays(new Date(), 29), new Date())}
        >
          Last 30 Days
        </button>
      </div>

      {/* Dual Calendar */}
      <div className="flex gap-4 p-4">
        {renderCalendar(prevMonth, leftDays)}
        {renderCalendar(currentMonth, rightDays)}
      </div>
    </div>
  );
};

export default DateRangeCalendar;
